import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { containerVariants, names } from 'Constants';
import { useBusinessPartnerInvoice } from 'Hooks';
import { Colors, IBadgeStatus } from 'Types';
import {
	clx,
	CommunityPartnerCreditStatus,
	currencyFormatter,
	formatLocaleDateTime,
	invoiceReason,
	invoiceStatus,
	unslug
} from 'Utils';
import {
	DataTable,
	DataTableBody,
	DataTableHead,
	DataTableLoading,
	DataTableRow,
	DataTableRowCell,
	NoData,
	Pagination,
	ListingPage,
	Button,
	EndOfList,
	SystemErrorAlert,
	Badge,
	FontAwesome,
	PageHeadingFilter
} from 'Elements';
import styles from 'partials/page/transaction.module.scss';

export const ListInvoice = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { invoices } = names;
	const [searchParams, setSearchParams] = useSearchParams();
	const params = Object.fromEntries([...searchParams]);
	const pageParam = searchParams.get('page') || 1;
	const queryParam = searchParams.get('q') || '';
	const statusParam = unslug(searchParams.get('status') ?? '') ?? '';
	const reasonParam = unslug(searchParams.get('reason') ?? '') ?? '';
	const [state, setState] = useState({
		pageSize: 20,
		totalItems: 0,
		currentPage: pageParam,
		keyword: queryParam,
		status: statusParam,
		reason: reasonParam
	});

	const getStatusData = (status) => {
		return CommunityPartnerCreditStatus.find(
			(elem) => elem.value === status
		);
	};

	const { data, isError, isFetching } = useBusinessPartnerInvoice(
		{
			pageSize: state.pageSize,
			pageNumber: state.currentPage,
			keyword: state.keyword,
			subscriptionId: '',
			reason: state.reason,
			status: state.status
		},
		{
			onSuccess: (data) => {
				setState((prev) => ({
					...prev,
					totalItems: data.totalItems
				}));
			}
		}
	);

	const onChangePageSize = (pageSize: number) => {
		setSearchParams({ ...params, page: '1' });
		setState((prev) => ({
			...prev,
			pageSize,
			currentPage: 1
		}));
	};

	const handleSearch = (query: string) => {
		setSearchParams({
			...params,
			page: '1',
			q: query
		});
		setState((prev) => ({
			...prev,
			currentPage: 1,
			keyword: query
		}));
	};

	const getStatusByName = (filter_name: string, filterObj: any) => {
		return filterObj.find((filter: any) => filter.title == filter_name);
	};

	const handlePageChange = (page: number) => {
		setSearchParams({ ...params, page: page.toString() });
		setState((prev) => ({
			...prev,
			currentPage: page
		}));
	};

	const handleFilter = (
		selected_filter: Partial<IBadgeStatus>,
		type: string
	) => {
		setSearchParams({
			...params,
			page: '1',
			[type]: selected_filter.title?.toString() ?? ''
		});
		setState((prev) => ({
			...prev,
			currentPage: 1,
			[type]: selected_filter.value?.toString() ?? ''
		}));
	};

	const redirectTo = (id: string) => {
		void navigate(`${invoices.path}/${id}`, {
			state: { prevPath: `${invoices.path}` }
		});
	};

	const cellClassName = (col: string) => {
		const cols: { [x: string]: string } = {
			'invoice-number': 'w-sm-20',
			product: 'w-sm-25',
			'created-at': 'w-sm-15',
			amount: 'w-sm-15',
			status: 'w-sm-10',
			operation: 'w-sm-15'
		};
		return clx('w-100', cols[col.toLowerCase()]);
	};

	const getReasonLabel = (reason) => {
		let message = '';
		switch (reason) {
			case 0:
				message = 'label.new-subscription';
				break;
			case 1:
				message = 'label.renew-subscription';
				break;
			case 2:
				message = 'label.upgrade-subscription';
				break;
			default:
				break;
		}

		return message;
	};

	return (
		<ListingPage
			totalItems={data?.totalItems}
			pageSizes={[10, 15, 20, 50]}
			selectedPageSize={state.pageSize}
			onPageSizeChange={onChangePageSize}
			onSearchChange={handleSearch}>
			<div className="d-flex align-items-center flex-wrap mb-3 gap-3">
				<PageHeadingFilter
					filterLabel="dropdown.status"
					filters={invoiceStatus}
					selectedFilter={t(
						getStatusByName(statusParam, invoiceStatus)?.label ??
							'dropdown.all'
					)}
					onChange={(selected) => handleFilter(selected, 'status')}
				/>
				<PageHeadingFilter
					filterLabel="dropdown.reason"
					filters={invoiceReason}
					selectedFilter={t(
						getStatusByName(reasonParam, invoiceReason)?.label ??
							'dropdown.all'
					)}
					onChange={(selected) => handleFilter(selected, 'reason')}
				/>
			</div>
			<DataTable>
				{state?.totalItems > 0 && (
					<DataTableHead>
						<DataTableRowCell
							cellText="label.invoice-number"
							cellClassName={cellClassName('invoice-number')}
						/>
						<DataTableRowCell
							cellText="table.product"
							cellClassName={cellClassName('product')}
						/>
						<DataTableRowCell
							cellText="table.created-at"
							cellClassName={cellClassName('created-at')}
						/>
						<DataTableRowCell
							cellText="table.amount"
							cellClassName={cellClassName('amount')}
						/>
						<DataTableRowCell
							cellText="table.status"
							cellClassName={cellClassName('status')}
						/>
						<DataTableRowCell
							cellText="table.operation"
							cellClassName={cellClassName('operation')}
						/>
					</DataTableHead>
				)}
				<DataTableBody>
					{!isFetching && (
						<motion.div
							variants={containerVariants}
							initial="hidden"
							animate="show">
							{data?.items?.map((transaction) => {
								return (
									<DataTableRow key={transaction?.id}>
										<DataTableRowCell
											cellClassName={cellClassName(
												'invoice-number'
											)}
											cellHead="table.invoice-number"
											cellText={
												transaction?.invoiceInfo
													?.invoiceNumber
											}
										/>
										<DataTableRowCell
											cellClassName={cellClassName(
												'product'
											)}
											cellHead="table.product">
											<div className="fw-500 mb-1">
												{
													transaction
														?.subscriptionPlanInfo
														?.name
												}
											</div>
											<Badge
												color={Colors.green}
												className="gap-2">
												{t(
													getReasonLabel(
														transaction?.reason
													)
												)}
											</Badge>
										</DataTableRowCell>
										<DataTableRowCell
											cellClassName={cellClassName(
												'created-at'
											)}
											cellHead="table.created-at">
											<time
												title={formatLocaleDateTime(
													transaction?.createdAt
												)}
												dateTime={formatLocaleDateTime(
													transaction?.createdAt
												)}>
												{formatLocaleDateTime(
													transaction?.createdAt
												)}
											</time>
										</DataTableRowCell>
										<DataTableRowCell
											cellClassName={cellClassName(
												'amount'
											)}
											cellHead="table.amount">
											<div className="d-flex flex-column text-end text-sm-start">
												<strong
													className="fs-base"
													title={currencyFormatter(
														transaction?.price
															?.totalAmount
													)}>
													{currencyFormatter(
														transaction?.price
															?.totalAmount
													)}
												</strong>
												<span className="text-gray-3 lh-base fs-small">
													{t('label.tax-included')}
												</span>
											</div>
										</DataTableRowCell>
										<DataTableRowCell
											cellClassName={cellClassName(
												'status'
											)}
											cellHead="table.status">
											<Badge
												color={
													getStatusData(
														transaction?.status
													)?.color ?? Colors.primary
												}>
												{t(
													`${
														getStatusData(
															transaction?.status
														)?.label
													}`
												)}
											</Badge>
										</DataTableRowCell>
										<DataTableRowCell
											cellHead="table.operation"
											cellClassName={cellClassName(
												'operation'
											)}
											boxClassName="d-flex flex-wrap gap-2">
											<Button
												size="sm"
												color={Colors['white-primary']}
												label={
													t(
														'button.details'
													) as string
												}
												onClick={() =>
													redirectTo(transaction.id)
												}
											/>
											{transaction?.invoiceInfo
												?.invoiceDocumentUrl && (
												<a
													href={
														transaction?.invoiceInfo
															?.invoiceDocumentUrl
													}
													rel="noreferrer"
													target="_blank"
													className={styles.button}
													download={`jobbiplace-invoice_${transaction?.invoiceInfo?.invoiceNumber}.pdf`}>
													<FontAwesome
														icon="download"
														size="lg"
													/>
												</a>
											)}
										</DataTableRowCell>
									</DataTableRow>
								);
							})}
						</motion.div>
					)}
					{isFetching && (
						<DataTableLoading
							widths={[20, 25, 15, 15, 10, 15]}
							count={state.pageSize}
						/>
					)}
				</DataTableBody>
				{isError && <SystemErrorAlert />}
				{!isFetching && data?.totalItems === 0 && (
					<NoData message="title.no-invoice" />
				)}
			</DataTable>
			{!isFetching && <EndOfList data={data} />}
			{!isFetching && data && data?.totalPages > 1 && (
				<Pagination
					className="mt-3"
					totalItemsCount={data?.totalItems}
					activePage={parseInt(state.currentPage.toString(), 10)}
					itemsCountPerPage={state.pageSize}
					onChange={handlePageChange}
				/>
			)}
		</ListingPage>
	);
};
